import { ITEM_DATABASE } from '../../../../shared/content/generated/items.gen';
import { ITEM_SLOT_LABELS, type ItemEquipmentSlot } from '../../../../shared/equipmentSlots';
import type { InventoryItem } from '../../../../shared/CharacterTypes';
import type { ItemData } from '../../components/ItemKit';
import { ACCENT, RARITY, formatCallingWithArchetype } from '../../theme';

export interface EquipmentSlotDefinition {
  id: ItemEquipmentSlot;
  label: string;
  /** `center` sits under the character preview (the Flame and the Lantern). */
  side: 'left' | 'right' | 'center';
}

// Labels come from ITEM_SLOT_LABELS (keys are storage, labels presentation).
// Lane BD (D46): `shoes` reads Trail and sits last on the right — it styles
// the ghost's light trail rather than dressing a body part; Hands moved left
// to keep four slots a side. Lane BK (B26): the Lantern is a ninth slot, so it
// sits centred under the preview — the light the ghost carries — and the
// four-a-side columns stay as they were.
export const EQUIPMENT_SLOTS = [
  { id: 'hat', label: ITEM_SLOT_LABELS.hat, side: 'left' },
  { id: 'top', label: ITEM_SLOT_LABELS.top, side: 'left' },
  { id: 'bottom', label: ITEM_SLOT_LABELS.bottom, side: 'left' },
  { id: 'gloves', label: ITEM_SLOT_LABELS.gloves, side: 'left' },
  { id: 'weapon', label: ITEM_SLOT_LABELS.weapon, side: 'right' },
  { id: 'cape', label: ITEM_SLOT_LABELS.cape, side: 'right' },
  { id: 'accessory', label: ITEM_SLOT_LABELS.accessory, side: 'right' },
  { id: 'shoes', label: ITEM_SLOT_LABELS.shoes, side: 'right' },
  { id: 'flame', label: ITEM_SLOT_LABELS.flame, side: 'center' },
  { id: 'lantern', label: ITEM_SLOT_LABELS.lantern, side: 'center' },
] as const satisfies readonly EquipmentSlotDefinition[];

const ITEM_DRAG_MIME = 'application/x-helix-item';

export interface DraggedInventoryItem {
  itemId: string;
  name: string;
  equipmentSlot: ItemEquipmentSlot | null;
}

export type EquipmentDropState = 'idle' | 'available' | 'valid' | 'invalid';
export type CharacterTab = 'equipment' | 'stats';
export function humanize(value: string): string {
  return value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^\w|\s\w/g, character => character.toUpperCase());
}

export function slotDefinition(slotId: ItemEquipmentSlot): EquipmentSlotDefinition {
  return EQUIPMENT_SLOTS.find(slot => slot.id === slotId)!;
}

export function itemEquipmentSlot(itemId: string): ItemEquipmentSlot | null {
  const item = ITEM_DATABASE[itemId];
  if (!item || (item.type !== 'weapon' && item.type !== 'armor')) return null;
  return item.equipmentSlot ?? null;
}

/**
 * Why this hero cannot wear an item, or null. The same checks, in the same order, as the server's
 * equipItem (src/server/utils/EquipmentUtils.ts), which still decides. An unknown hero blocks
 * nothing, so a loading sheet never refuses.
 */
export function equipBlock(itemId: string, hero: { level?: number; class?: string } | null | undefined): string | null {
  const item = ITEM_DATABASE[itemId];
  if (!item || !hero) return null;
  if (typeof hero.level === 'number' && hero.level < item.requiredLevel) return `Requires level ${item.requiredLevel}`;
  if (item.requiredClass && hero.class && hero.class !== item.requiredClass) {
    return `Requires ${formatCallingWithArchetype(item.requiredClass)} class`;
  }
  return null;
}

export function rarityTone(item: ItemData | null): string {
  if (!item?.rarity) return ACCENT.brass;
  return RARITY[item.rarity] ?? ACCENT.brass;
}

export function formatItemStat(value: number): string {
  if (value > 0) return `+${value}`;
  return String(value);
}

/** Read both the typed Helix payload and the interoperable text fallback. */
export function readDraggedItemId(dataTransfer: Pick<DataTransfer, 'getData'>): string | null {
  try {
    const typed = dataTransfer.getData(ITEM_DRAG_MIME);
    if (typed) return typed;
    const plain = dataTransfer.getData('text/plain');
    return plain.startsWith('item:') ? plain.slice('item:'.length) || null : null;
  } catch {
    return null;
  }
}

export function dropToast(message: string): void {
  try {
    (globalThis as unknown as {
      __errorToast?: (level: 'error' | 'warn' | 'info', text: string) => void;
    }).__errorToast?.('info', message);
  } catch {
    // Toasts are optional in isolated UI tests and early boot.
  }
}


export function compatibleInventoryItems(
  items: readonly InventoryItem[],
  selectedSlot: ItemEquipmentSlot,
): InventoryItem[] {
  return items.filter(item => itemEquipmentSlot(item.id) === selectedSlot);
}
